<template lang="pug">
.page.page--light.logistics-companies(:class="{ submitted: submittedBOIForm || loading.submitted }")
  .page__backdrop(v-if="!submittedBOIForm && !loading.submitted")
  .page__inner
    .page__section
      .container.framed
        .section(v-if="!submittedBOIForm && !loading.submitted")
          .cover
            img(src="/img/boi/cover.webp" width="584" height="148" alt="Box Of Innovation (BOI) Program")
            h1 Box Of Innovation (BOI) Program
            p The Pod Box Of Innovation (BOI) Program curates and consolidates high-potential brands in a stunning, glimmering box and ships them to our retail partners. Products in these boxes have had great success in attaining retail placements in retailers such as Sprouts Farmers’ Market, Albertsons, Whole Foods and more.
            p The Pod Team combines years of industry experience together with our proprietary data insights to curate the selection of high-potential brands for each box. These boxes are customized to meet the retailer’s current needs and objectives, and allows the selected brands to target the right placement at the right time.
            p Fewer than 2% of our brands are selected each month to be a part of the BOI program.
            p If you think you have what it takes to be part of our BOI program, we encourage you to reach out to us by filling out the form below.

          .boi-form
            el-form.form(
              :rules="rules"
              :model="form"
              ref="form"
              @submit.prevent)
              el-form-item(prop="brand_id" v-if="isVendor")
                .title
                  span.asterisk Brand name
                el-select.pf-expanded(
                  ref="brandSelect"
                  :remote-method="getBrands"
                  placeholder="Select or type to search"
                  v-model="form.brand_id"
                  default-first-option
                  filterable
                  @visible-change="handleVisibleChange")
                  el-option(
                    v-for="i, k in brands"
                    :key="i.id"
                    :label="i.name"
                    :value="i.id")
              el-form-item(
                v-else
                prop="manual_brand_name")
                .title
                  span.asterisk Brand name
                el-input(
                  v-model="form.manual_brand_name"
                  placeholder="Enter")

              el-form-item(prop="manual_email")
                .title
                  span.asterisk Email address
                el-input(
                  type="email"
                  v-model="form.manual_email"
                  placeholder="Enter"
                  :readonly="isVendor")
              el-form-item(prop="instagram_channels")
                .title Instagram handle
                social-handle-input(
                  v-model.trim="form.instagram_channels"
                  placeholder="username"
                  icon="bxl-instagram"
                  prefix="https://instagram.com/"
                  name="Instagram"
                  linked)
              el-form-item(prop="instagram_followers")
                .title Instagram number of followers
                el-input(
                  v-model="form.instagram_followers"
                  placeholder="Enter"
                  type="number"
                  min="0"
                  step="1")
              el-form-item(prop="tiktok_channels")
                .title Tiktok handle
                social-handle-input(
                  v-model.trim="form.tiktok_channels"
                  placeholder="username"
                  icon="bxl-tiktok"
                  prefix="https://tiktok.com/@"
                  name="Tiktok"
                  linked)
              el-form-item(prop="tiktok_followers")
                .title Tiktok number of followers
                el-input(
                  v-model="form.tiktok_followers"
                  placeholder="Enter"
                  type="number"
                  min="0"
                  step="1")

              el-form-item(prop="total_annual_dtc_sales")
                .title Total annual DTC sales
                .sub-title E.g. Shopify sales
                el-input.mt-1(placeholder="Enter" v-model="form.total_annual_dtc_sales" :maxlength="256")

              el-form-item(prop="comment")
                .title Any other comments
                el-input(type="textarea" :rows="5" placeholder="Enter" v-model="form.comment")

              el-button.pf-expanded.submit(
                :loading="loading.submit"
                @click="submit"
                type="primary") Submit

        .section.submitted(v-else)
          lottie(:options="animation")
          .title Thank you for your submission.
          .desc You will be notified of your application status within a week.
</template>

<script>
import Lottie from 'vue-lottie'
import { mapGetters } from 'vuex'
import FileInput from '~/components/inputs/FileInput'
import SocialHandleInput from '~/components/inputs/SocialHandleInput'
import { mapSocialNetworks } from '~/utilities/normalizers'

export default {
  components: {
    FileInput,
    SocialHandleInput,
    Lottie
  },

  scrollToTop: true,

  data () {
    return {
      brands: [],
      form: {
        manual_email: '',
        instagram_channels: '',
        tiktok_channels: '',
        total_annual_dtc_sales: '',
        manual_brand_name: ''
      },
      loading: {
        submitted: false,
        submit: false
      }
    }
  },

  fetch () {
    if (!this.isVendor) return

    this.form.manual_email = this.user.email

    return this
      .getBrands()
      .then(brands => {
        if (brands.length === 1) this.$set(this.form, 'brand_id', brands[0].id)
      })
  },

  head () {
    return this.$seo.head({
      title: 'Box Of Innovation (BOI) Program'
    })
  },

  computed: {
    ...mapGetters({
      user: 'authentication/user',
      isVendor: 'authentication/isVendor',
      submittedBOIForm: 'authentication/submittedBOIForm'
    }),
    animation () {
      return {
        animationData: require('~/assets/lotties/order-placed.json'),
        animationSpeed: 1,
        loop: true,
        autoplay: 1,
        small: true
      }
    },
    rules () {
      const { required, email } = this.$validators

      return {
        brand_id: required,
        manual_brand_name: required,
        manual_email: [required, email]
      }
    }
  },

  watch: {
    'form.brand_id' (id) {
      if (!this.isVendor) return

      const brand = this.brands.find(e => e.id === +id)
      this.form.manual_brand_name = brand.name

      this.getBrand(id)
    }
  },

  methods: {
    handleVisibleChange (visible) {
      if (visible) return
      this.$refs.brandSelect.blur()
    },

    getBrand (id) {
      this.form.tiktok_channels = ''
      this.form.instagram_channels = ''

      this.$api.vendor.getBrand({ id })
      .then(({ brand }) => {
        if (!brand?.social_networks_attributes.length) return

        const networks =
          mapSocialNetworks(brand.social_networks_attributes)
            .filter(e => e.username && ['tiktok', 'instagram'].includes(e.network))
            .map(e => ({ ...e, link: `${e.prefix}${e.username}` }))

        networks.forEach(e => {
          if (e.network === 'tiktok') this.form.tiktok_channels = e.username
          if (e.network === 'instagram') this.form.instagram_channels = e.username
        })
      })
    },

    getBrands (q) {
      return this.$api.vendor.getBrands({ 'q[name]': q })
        .then(res => {
          this.brands = res.results || []
          return res.results
        })
    },

    submit () {
      this.$validate(this.$refs.form)
        .then(ok => {
          if (!ok) return

          this.loading.submit = true

          return this.$api.vendor
            .createBoxOfInnovation({
              box_of_innovation: {
                ...this.form
              }
            })
            .then(() => {
              this.loading.submitted = true
              this.$nextTick(() => {
                window?.scrollTo({ top: 0, behavior: 'smooth' })
              })
            })
            .finally(() => {
              this.loading.submit = false
            })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
$bg: darken($--color-page-background, 15);
.page {
  background: $--color-accent-a-light;

  &.submitted {
    background: white;
  }
}
.section {
  max-width: 680px;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  margin: 0 auto 50px auto;
  &.submitted {
    max-width: 500px;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    .title {
      display: flex;
      justify-content: center;
      font-size: 2em;
      color: $--color-primary;
      margin: 1em 0;
    }
    .desc {
      display: flex;
      justify-content: center;
      font-size: 1.2em;
    }
  }
  .cover {
    padding: 32px 32px 0;
    img {
      width: 100%;
      height: auto;
      border-radius: 12px;
    }
    h1 {
      margin-top: 1.25em;
      font-size: 24px;
      line-height: 135%;
    }
    p {
      font-size: 14px;
      line-height: 150%;
    }
  }
  .boi-form {
    padding: 2rem;
    .title {
      font-weight: $--font-weight-bold;
      margin-bottom: 0.5rem;
    }
    .sub-title {
      color: $--color-primary;
      font-size: 13px;
    }
    ::v-deep {
      .el-input__inner[readonly] {
        background-color: lighten(#f5f7fa, 2);
        border-color: #e4e7ed;
        color: #606266;
        cursor: default;
      }
    }
  }
  .brand-condition {
    padding: 2rem 1.5rem;
    background-color: #F4F4FD;
    border-radius: $--radius-lg;
    margin-bottom: 2rem;
    .step {
      display: flex;
      font-weight: $--font-weight-bold;
    }
    .content {
      width: 100%;
    }
    .progress {
      display: grid;
      grid-template-columns: 1fr 60px;
      grid-template-areas: 'bar label';
      margin-top: 10px;
      &--bar {
        grid-area: bar;
        ::v-deep .el-progress-bar__outer {
          background-color: $bg;
        }
        .clp {
          border-radius: 100px;
          height: 20px;
          background-color: $bg !important;
        }
      }
      &--label {
        grid-area: label;
        font-weight: $--font-weight-bold;
        margin-left: 16px;
      }
    }
  }
}
</style>
